/**
 * LoadDraftButton Component
 * 
 * A compact icon-based component for draft data management.
 * Shows load and clear buttons when local draft data exists.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { IconButton, Tooltip, Box, Chip } from '@mui/material';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import StorageIcon from '@mui/icons-material/Storage';
import Swal from 'sweetalert2';

const LoadDraftButton = ({ 
  hasDraftData, 
  onLoadDraft,
  onClearDraft,
  timestamp,
  disabled = false,
  showLabel = false,
}) => {
  // Don't render if no draft data
  if (!hasDraftData) {
    return null;
  }

  const loadTooltip = timestamp 
    ? `Load saved draft (${timestamp})` 
    : 'Load saved draft';

  const clearTooltip = 'Clear local draft';

  const handleClearClick = async () => {
    if (!onClearDraft) return;
    
    const result = await Swal.fire({
      title: 'Clear Local Draft?',
      text: 'This will permanently delete your saved draft data.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d32f2f',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, clear it!',
      cancelButtonText: 'Cancel',
    });

    if (result.isConfirmed) {
      await onClearDraft();
      Swal.fire({
        icon: 'success',
        title: 'Cleared!',
        text: 'Local draft has been deleted.',
        timer: 1500,
        showConfirmButton: false,
        toast: true,
        position: 'top-end',
      });
    }
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      alignItems: 'center', 
      gap: 0.5,
      backgroundColor: 'rgba(0, 121, 142, 0.08)',
      borderRadius: '20px',
      padding: '4px 8px',
      border: '1px solid rgba(0, 121, 142, 0.3)',
    }}>
      {/* Draft indicator chip */}
      <Chip
        icon={<StorageIcon sx={{ fontSize: '14px !important' }} />}
        label={showLabel ? 'Draft available' : ''}
        size="small"
        sx={{
          backgroundColor: 'transparent',
          color: '#00798E',
          fontSize: '11px',
          fontWeight: 500,
          height: '24px',
          '& .MuiChip-icon': {
            color: '#00798E',
            marginLeft: showLabel ? '4px' : '0px',
            marginRight: showLabel ? '-4px' : '0px',
          },
          '& .MuiChip-label': {
            padding: showLabel ? '0 8px' : '0 4px',
          },
        }}
      />
      
      {/* Load draft button */}
      <Tooltip title={loadTooltip} arrow placement="top">
        <IconButton
          size="small"
          onClick={onLoadDraft}
          disabled={disabled}
          sx={{
            color: '#00798E',
            backgroundColor: 'rgba(0, 121, 142, 0.1)',
            width: '28px',
            height: '28px',
            '&:hover': {
              backgroundColor: 'rgba(0, 121, 142, 0.25)',
              transform: 'scale(1.1)',
            },
            transition: 'all 0.2s ease',
          }}
        >
          <CloudDownloadIcon sx={{ fontSize: '16px' }} />
        </IconButton>
      </Tooltip>
      
      {/* Clear draft button */}
      {onClearDraft && (
        <Tooltip title={clearTooltip} arrow placement="top">
          <IconButton
            size="small"
            onClick={handleClearClick}
            disabled={disabled}
            sx={{
              color: '#d32f2f',
              backgroundColor: 'rgba(211, 47, 47, 0.08)',
              width: '28px',
              height: '28px',
              '&:hover': {
                backgroundColor: 'rgba(211, 47, 47, 0.2)',
                transform: 'scale(1.1)',
              },
              transition: 'all 0.2s ease',
            }}
          >
            <DeleteOutlineIcon sx={{ fontSize: '16px' }} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};

LoadDraftButton.propTypes = {
  hasDraftData: PropTypes.bool.isRequired,
  onLoadDraft: PropTypes.func.isRequired,
  onClearDraft: PropTypes.func,
  timestamp: PropTypes.string,
  disabled: PropTypes.bool,
  showLabel: PropTypes.bool,
};

export default LoadDraftButton;
